import axios, { type AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { type ModuleType, type ReadCallback } from 'i18next'

type TranslationsDict = { [key: string]: string | TranslationsDict }
type TrackTraceTranslationsResponse = {
  events: TranslationsDict
}

type Props = {
  apiGatewayHost: string
}

type FetchOptions = {
  url: string
  signal?: AbortSignal
}

const timeout = 10000
const maxRetries = 5
const retryDelay = 100

function isTransientError(error: AxiosError): boolean {
  // Network errors (no response received)
  if (!error.response) {
    return true
  }

  // Retry on server errors (5xx) and rate limiting (429)
  const { status } = error.response
  return status >= 500 || status === 429
}

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

async function attemptFetch<O>(config: AxiosRequestConfig, attempt = 0): Promise<O> {
  try {
    const response: AxiosResponse<O> = await axios.request<O>(config)
    return response.data
  } catch (error) {
    const axiosError = error as AxiosError

    // Don't retry if the request was aborted
    if (axiosError.code === 'ERR_CANCELED') {
      throw axiosError
    }

    // Only retry on transient errors and if we haven't exhausted retries
    if (!isTransientError(axiosError) || attempt >= maxRetries) {
      throw axiosError
    }

    // Exponential backoff: delay * 2^attempt
    await delay(retryDelay * 2 ** attempt)

    return attemptFetch<O>(config, attempt + 1)
  }
}

async function fetchWithRetry<O>({ url, signal }: FetchOptions): Promise<O> {
  const config: AxiosRequestConfig = {
    url,
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
    signal,
    timeout,
  }

  return attemptFetch<O>(config)
}

export const trackTraceTranslationsI18nBackend = ({ apiGatewayHost }: Props) => {
  let abortController: AbortController | null = null

  return {
    type: 'backend' as ModuleType,
    init: (): void => {},
    async read(language: string, namespace: string, callback: ReadCallback): Promise<void> {
      if (namespace === 'trackTrace') {
        // Abort any in-flight request for a previous language
        if (abortController) {
          abortController.abort()
        }
        abortController = new AbortController()

        // T&T files have not (yet) been renamed to the new format.
        // Thus, temporarily replacing the '-' with '_' in the file name here
        const updatedLocale = language.replace('-', '_')
        const url = `${apiGatewayHost}/translations/${updatedLocale}.json`

        try {
          const response = await fetchWithRetry<TrackTraceTranslationsResponse>({
            url,
            signal: abortController.signal,
          })
          callback(null, {
            trackTrace: response.events,
          })
        } catch (e) {
          const axiosError = e as AxiosError
          // Don't report aborted requests as errors
          if (axiosError.code === 'ERR_CANCELED') {
            return
          }
          callback(e as Error, null)
        }
      } else {
        callback(null, null)
      }
    },
  }
}
